<script setup>
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useJobDataStore } from '../../stores/job-store';
import AppIcon from '../atoms/app/AppIcon.vue';
import { CV_TYPES, useApplicationStore } from '../../stores/application-store';
import AppLink from '../atoms/app/AppLink.vue';
import FileListEntry from './fileList/FileListEntry.vue';
import { track } from '../../utils/tracking';
import { PAGE_SUCCESS } from '../../routes';
import { useBreakpoints } from '../../composables/breakpoint';
import CompanyLogoBox from './CompanyLogoBox.vue';
import CustomFieldsPreview from './CustomFieldsPreview.vue';

defineProps({
    isSuccessPage: {
        type: Boolean,
        default: false,
    },
});

const { isMobile } = useBreakpoints();

const jobInfo = useJobDataStore();
const application = useApplicationStore();

const { selectedCvType } = storeToRefs(application);

// Relevant for tablet + up
const isExpanded = ref(false);

const route = useRoute();
const handleExpanding = () => {
    if (!isExpanded.value && route.name === PAGE_SUCCESS) {
        track({
            element: 'AP_F: Successpage',
            elementDetail: 'Expand summary desktop',
        });
    }
    isExpanded.value = !isExpanded.value;
};
</script>

<template>
    <div
        class="c-applicationSummary relative"
        :class="isSuccessPage && 'bg-white m:bg-inherit z-10 m:z-0'"
        v-bind="$attrs"
    >
        <div
            class="m:p-2xl m:border-solid border-gray-900 border rounded-md m:overflow-y-hidden flex flex-col"
            :class="{
                'm:h-[24rem]': isSuccessPage && !isExpanded,
                'border-solid border-gray-100 border rounded p-l mt-xl mb-l m:mb-0': isSuccessPage,
                'p-10 border-solid border-gray-100 border rounded': !isSuccessPage,
                'm:p-3xl': !isSuccessPage,
            }"
        >
            <div class="flex justify-between">
                <!-- Personal Data -->
                <div>
                    <span>Von {{ application.firstname }} {{ application.lastname }}</span>
                    <br>
                    <span class="text-gray-600 text-s">{{ application.email }}</span>
                </div>
            </div>

            <!-- Job application info -->
            <div
                v-if="isSuccessPage"
            >
                <hr class="bg-gray-100 my-l m:my-xl">
                <h3 class="font-bold mb-m">
                    Bewerbung als ...
                </h3>
                <div class="flex items-center gap-l">
                    <CompanyLogoBox
                        v-if="!isMobile"
                        class="w-4xl h-4xl"
                    />
                    <div>
                        <AppLink
                            :href="jobInfo.link"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-gtm-element="AP_F: Successpage"
                            data-gtm-element-detail="Open job ad"
                        >
                            {{ jobInfo.title }}
                        </AppLink>
                        <p class="text-gray-600">
                            {{ jobInfo.companyName }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- CV -->
            <div v-if="selectedCvType !== CV_TYPES.NONE">
                <hr class="bg-gray-200 my-xl">
                <h3 class="font-bold flex-grow mb-l">
                    Lebenslauf
                </h3>
                <FileListEntry
                    :file="application.selectedCv"
                    :hide-cv-create-date="isSuccessPage"
                    :create-date-only="!isSuccessPage"
                    :small-gaps="!isSuccessPage"
                    is-preview
                />
            </div>

            <!-- Attachments -->
            <div
                v-if="application.hasActiveAttachments"
                class="flex flex-col"
            >
                <hr class="bg-gray-200 my-xl">
                <h3 class="font-bold flex-grow mb-l">
                    Sonstige Dokumente
                </h3>
                <div class="flex flex-col gap-l">
                    <!-- active attachments -->
                    <div
                        v-for="activeAttachment in application.activeAttachments"
                        :key="activeAttachment.uuid ?? activeAttachment.name"
                        class="o-fileUpload__listEntry"
                        :data-qa="activeAttachment.isCv ? 'cv box has profile' : 'attachment'"
                    >
                        <FileListEntry
                            :file="activeAttachment"
                            :hide-cv-create-date="isSuccessPage"
                            :create-date-only="!isSuccessPage"
                            :small-gaps="!isSuccessPage"
                            is-preview
                        />
                    </div>
                </div>
            </div>

            <!-- Application letter -->
            <div
                v-if="application.applicationLetter"
                class="whitespace-pre-wrap"
            >
                <hr class="bg-gray-200 my-xl">
                <h3 class="font-bold flex-grow mb-l">
                    Anmerkungen zur Bewerbung
                </h3>
                <p class="[word-break:break-word]">
                    {{ application.applicationLetter }}
                </p>
            </div>

            <div v-if="jobInfo.hasCustomFields">
                <hr class="bg-gray-100 my-l m:my-xl">
                <CustomFieldsPreview
                    :custom-fields="jobInfo.customFields"
                    class="z-10 flex flex-col gap-m"
                    data-qa="custom fields preview"
                />
            </div>
        </div>
        <div
            v-if="!isExpanded && isSuccessPage"
            class="hidden pointer-events-none m:block absolute bg-gradient-to-t from-white h-5xl bottom-0 left-0 w-full"
            :class="isSuccessPage ? 'h-5xl' : 'h-3xl'"
        />
    </div>
    <div
        v-if="isSuccessPage"
        class="hidden m:block mt-l"
    >
        <AppLink
            tag="button"
            type="button"
            data-qa="application summary toggle"
            @click="handleExpanding"
        >
            <template v-if="isExpanded">
                Weniger anzeigen
            </template>
            <template v-else>
                Mehr anzeigen
            </template>
            <AppIcon
                name="arrow-down"
                class="ml-m transition-transform"
                :class="isExpanded && 'rotate-180'"
            />
        </AppLink>
    </div>
</template>

<style lang="scss">

</style>
